(function () {
  function initProductCarousel(root) {
    if (!root || root.dataset.nazvaProductCarouselReady === 'true') return;

    const track = root.querySelector('[data-nazva-product-carousel-track]');
    const prev = root.querySelector('[data-nazva-product-carousel-prev]');
    const next = root.querySelector('[data-nazva-product-carousel-next]');

    if (!track) return;

    root.dataset.nazvaProductCarouselReady = 'true';

    const usesNativeTouchScroll = () =>
      window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(hover: none)').matches;

    const updateState = () => {
      const maxScrollLeft = track.scrollWidth - track.clientWidth;
      const hasOverflow = maxScrollLeft > 2;
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      const shouldShowControls =
        hasOverflow && (isDesktop ? root.dataset.desktopControls === 'true' : root.dataset.mobileControls === 'true');

      root.classList.toggle('has-carousel-controls', shouldShowControls);
      root.classList.toggle('is-scrollable', hasOverflow);

      if (!prev || !next) return;

      prev.disabled = !hasOverflow || track.scrollLeft <= 2;
      next.disabled = !hasOverflow || track.scrollLeft >= maxScrollLeft - 2;
    };

    const getMaxScrollLeft = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const clampScrollLeft = (value) => Math.min(Math.max(value, 0), getMaxScrollLeft());

    const getItemScrollPositions = () => {
      const items = Array.from(track.querySelectorAll('[data-nazva-product-carousel-item]'));
      const trackLeft = track.getBoundingClientRect().left;
      const trackPaddingLeft = Number.parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
      const positions = items.map((item) =>
        clampScrollLeft(item.getBoundingClientRect().left - trackLeft + track.scrollLeft - trackPaddingLeft)
      );

      return Array.from(new Set([0, ...positions, getMaxScrollLeft()].map((value) => Math.round(value)))).sort((a, b) => a - b);
    };

    const getNearestItemScrollLeft = () => {
      let nearest = track.scrollLeft;
      let nearestDistance = Number.POSITIVE_INFINITY;

      getItemScrollPositions().forEach((position) => {
        const distance = Math.abs(track.scrollLeft - position);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = position;
        }
      });

      return clampScrollLeft(nearest);
    };

    const getAdjacentItemScrollLeft = (direction) => {
      const positions = getItemScrollPositions();
      const current = Math.round(track.scrollLeft);
      const tolerance = 6;

      if (direction > 0) {
        return positions.find((position) => position > current + tolerance) ?? positions[positions.length - 1] ?? current;
      }

      for (let index = positions.length - 1; index >= 0; index -= 1) {
        if (positions[index] < current - tolerance) {
          return positions[index];
        }
      }

      return positions[0] ?? current;
    };

    const snapToNearestItem = (behavior = 'smooth') => {
      if (getMaxScrollLeft() <= 2) {
        updateState();
        return;
      }

      track.scrollTo({
        left: getNearestItemScrollLeft(),
        behavior,
      });
      updateState();
    };

    const scrollByPage = (direction) => {
      track.scrollTo({
        left: getAdjacentItemScrollLeft(direction),
        behavior: 'smooth',
      });
    };

    prev?.addEventListener('click', () => scrollByPage(-1));
    next?.addEventListener('click', () => scrollByPage(1));

    let scrollFrame;
    track.addEventListener('scroll', () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(updateState);
    });

    if (!usesNativeTouchScroll()) {
      let isDragging = false;
      let startX = 0;
      let startScrollLeft = 0;
      let previousX = 0;
      let previousTime = 0;
      let velocity = 0;
      let momentumFrame;
      let hasMoved = false;
      let suppressNextClick = false;
      let suppressClickTimer;
      const dragThreshold = 8;

      const stopMomentum = () => {
        window.cancelAnimationFrame(momentumFrame);
        momentumFrame = undefined;
      };

      const runMomentum = () => {
        if (Math.abs(velocity) < 0.08) {
          snapToNearestItem();
          return;
        }

        const previousScrollLeft = track.scrollLeft;
        track.scrollLeft = clampScrollLeft(track.scrollLeft - velocity * 24);
        velocity *= 0.965;

        if (track.scrollLeft === previousScrollLeft) {
          snapToNearestItem();
          return;
        }

        momentumFrame = window.requestAnimationFrame(runMomentum);
      };

      track.addEventListener('pointerdown', (event) => {
        if (event.target.closest('button, input, select, textarea')) return;

        stopMomentum();
        isDragging = true;
        hasMoved = false;
        startX = event.clientX;
        startScrollLeft = track.scrollLeft;
        previousX = event.clientX;
        previousTime = performance.now();
        velocity = 0;
      });

      track.addEventListener('pointermove', (event) => {
        if (!isDragging) return;

        const now = performance.now();
        const moved = event.clientX - startX;
        const deltaX = event.clientX - previousX;
        const deltaTime = now - previousTime || 16;

        if (Math.abs(moved) > dragThreshold) {
          hasMoved = true;
        }

        if (!hasMoved) return;

        event.preventDefault();
        if (!track.hasPointerCapture(event.pointerId)) {
          track.setPointerCapture(event.pointerId);
        }
        track.classList.add('is-dragging');
        track.scrollLeft = startScrollLeft - moved;
        velocity = deltaX / deltaTime;
        previousX = event.clientX;
        previousTime = now;
      });

      const stopDragging = (event) => {
        if (!isDragging) return;

        isDragging = false;
        track.classList.remove('is-dragging');

        if (track.hasPointerCapture(event.pointerId)) {
          track.releasePointerCapture(event.pointerId);
        }

        if (!hasMoved) {
          velocity = 0;
          updateState();
          return;
        }

        suppressNextClick = true;
        window.clearTimeout(suppressClickTimer);
        suppressClickTimer = window.setTimeout(() => {
          suppressNextClick = false;
        }, 350);

        const dragDistance = track.scrollLeft - startScrollLeft;
        const isFlick = Math.abs(velocity) > 0.4;
        const isSignificantDrag = Math.abs(dragDistance) > track.clientWidth * 0.15;

        if (isFlick || isSignificantDrag) {
          const direction = dragDistance > 0 ? 1 : -1;
          const targetScrollLeft = getAdjacentItemScrollLeft(direction);
          
          requestAnimationFrame(() => {
            track.scrollTo({
              left: targetScrollLeft,
              behavior: 'smooth',
            });
          });
          
          updateState();
          return;
        }

        runMomentum();
      };

      track.addEventListener('pointerup', stopDragging);
      track.addEventListener('pointercancel', stopDragging);
      track.addEventListener('pointerleave', stopDragging);

      track.addEventListener(
        'click',
        (event) => {
          if (!suppressNextClick) return;
          event.preventDefault();
          event.stopPropagation();
          suppressNextClick = false;
          window.clearTimeout(suppressClickTimer);
        },
        true
      );
    }

    window.addEventListener('resize', updateState);
    updateState();
  }

  function initAll() {
    document.querySelectorAll('[data-nazva-product-carousel]').forEach(initProductCarousel);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', (event) => {
    initProductCarousel(event.target.querySelector('[data-nazva-product-carousel]'));
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node.matches('[data-nazva-product-carousel]')) {
          initProductCarousel(node);
        }

        node.querySelectorAll?.('[data-nazva-product-carousel]').forEach(initProductCarousel);

        const carousel = node.closest?.('[data-nazva-product-carousel]');
        if (carousel) {
          initProductCarousel(carousel);
        }
      });
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
